import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Link from "next/link";
import { CgArrowLeft } from "react-icons/cg";

import type {
  BriefingReportPageData,
  BriefingReportPageDataResponse,
} from "content-ui/briefings/fetchers/briefingReportFetcher";

import "react-medium-image-zoom/dist/styles.css";
import useSiftedMediaQueries from "lib/hooks/useSiftedMediaQueries";

import { useIntersectionObserver } from "./hooks/useIntersectionObserver";
import ContentNavigation from "./components/briefingsReports/elements/navigation/contentNavigation/contentNavigation";
import IntroCard from "./components/briefingsReports/elements/introCard";
import MapCard from "./components/briefingsReports/elements/mapCard";
import SiftedTakeCard from "./components/briefingsReports/elements/siftedTakeCard";
import TrendsCard from "./components/briefingsReports/elements/trendsCard";
import StarsCard from "./components/briefingsReports/elements/starsCard";
import SuccessCard from "./components/briefingsReports/elements/successCard";
import StatsCard from "./components/briefingsReports/elements/statsCard";
import SourcesCard from "./components/briefingsReports/elements/sourcesCard";
import MarketCard from "./components/briefingsReports/elements/marketCard";
import FeedbackCard from "./components/briefingsReports/elements/feedbackCard";
import StartupsTableCard from "./components/briefingsReports/elements/startupsTableCard";
import TrackedCard from "./components/briefingsReports/elements/trackedCard";
import DealsCard from "./components/briefingsReports/elements/dealsCard/dealsCard";

const innerContainerClasses = classNames(
  "grid justify-center gap-xl pb-4xl lg:pb-4xl"
);

type Section = {
  id: string;
  navTitle: string;
  component: (id: string, briefing: BriefingReportPageData) => React.ReactNode;
};

type TableOfContentProps = {
  sections: Section[];
  briefing: BriefingReportPageData;
};

const TableOfContents = ({ sections, briefing }: TableOfContentProps) => {
  const [activeId, setActiveId] = useState<string>("");
  useIntersectionObserver(setActiveId);

  return (
    <nav
      aria-labelledby="Briefings table of contents"
      className="lg:sticky lg:top-20 lg:mb-24"
    >
      <ContentNavigation
        briefing={briefing}
        sections={sections}
        activeId={activeId}
      />
    </nav>
  );
};

type BriefingReportViewProps = BriefingReportPageDataResponse & {
  blur: boolean;
};

const BriefingReportView = ({ briefing, blur }: BriefingReportViewProps) => {
  const { desktop } = useSiftedMediaQueries();

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const sections = [
    {
      id: "intro",
      navTitle: "Intro",
      component: (id: string, briefing: BriefingReportPageData) => (
        <IntroCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "101",
      navTitle: "101",
      component: (id: string, briefing: BriefingReportPageData) => (
        <MarketCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "map",
      navTitle: "Map",
      component: (id: string, briefing: BriefingReportPageData) => (
        <MapCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "deals",
      navTitle: "Deals",
      component: (id: string, briefing: BriefingReportPageData) => (
        <DealsCard briefing={briefing} id={id} />
      ),
    },
    {
      id: "facts",
      navTitle: "Key facts",
      component: (id: string, briefing: BriefingReportPageData) => (
        <StatsCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "trends",
      navTitle: "Trends",
      component: (id: string, briefing: BriefingReportPageData) => (
        <TrendsCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "stats",
      navTitle: "Stats",
      component: (id: string, briefing: BriefingReportPageData) => (
        <TrackedCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "our-take",
      navTitle: "Our take",
      component: (id: string, briefing: BriefingReportPageData) => (
        <SiftedTakeCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "stars",
      navTitle: "Stars",
      component: (id: string, briefing: BriefingReportPageData) => (
        <StarsCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "startups",
      navTitle: "Ones to watch",
      component: (id: string, briefing: BriefingReportPageData) => (
        <StartupsTableCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "successes",
      navTitle: "Successes",
      component: (id: string, briefing: BriefingReportPageData) => (
        <SuccessCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "sources",
      navTitle: "Sources",
      component: (id: string, briefing: BriefingReportPageData) => (
        <SourcesCard id={id} briefing={briefing} />
      ),
    },
    {
      id: "feedback",
      navTitle: "Your feedback",
      component: (id: string, briefing: BriefingReportPageData) => (
        <FeedbackCard id={id} briefing={briefing} />
      ),
    },
  ];

  const LinkToIndex = ({ variant }: { variant: "top" | "bottom" }) => {
    return (
      <div className="mb-[-10px] md:mb-0">
        <Link href="/pro/briefings" className="flex items-center">
          <CgArrowLeft className="mr-1" color="#DA145E" />
          <p
            className={classNames(
              "inline-block font-[700] leading-tight text-cerise md:text-[20px]",
              {
                "text-[16px]": variant === "top",
                "text-[20px]": variant === "bottom",
              }
            )}
          >
            All Briefings
          </p>
        </Link>
      </div>
    );
  };

  const filteredSections = sections.filter((section) => {
    // Don't show the success stories section if there are no success entries
    if (briefing.successStories.length === 0) return section.id !== "successes";

    return true;
  });

  return (
    <main className="bg-[#F8F7F9] font-ssp">
      {hasMounted && !desktop && (
        <TableOfContents briefing={briefing} sections={filteredSections} />
      )}
      <div
        className={classNames(
          "wrapper relative overflow-hidden lg:overflow-visible",
          { "blur-md": blur }
        )}
      >
        <div className="flex w-100 justify-center pt-[70px] md:pt-[80px] lg:pt-[60px]">
          <div className={innerContainerClasses}>
            <LinkToIndex variant="top" />
            {filteredSections.map((section) => (
              <div
                key={`content__${section.id}`}
                id={`content__${section.id}`}
                className="briefings__content"
              >
                {section.component(section.id, briefing)}
              </div>
            ))}
            <LinkToIndex variant="bottom" />
          </div>
          {hasMounted && desktop && (
            <aside className="mb-4 min-w-[162px] max-w-[210px] flex-col lg:relative lg:ml-6 lg:flex lg:w-1/5">
              <TableOfContents
                briefing={briefing}
                sections={filteredSections}
              />
            </aside>
          )}
        </div>
      </div>
    </main>
  );
};
export default BriefingReportView;
